/** ***********
*
* <dialog-generate-plannings-line.component.ts v1.0>
*
* Copyright (c) 2024 AIRBUS OPERATIONS
*
* All rights reserved.
**************/

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subscription, iif, of } from 'rxjs';
import { SnackbarComponent } from 'src/app/components/shared/snackbar/snackbar';
import { ErrorTranslationService } from 'src/app/services/error-translation.service';
import { DevToolsService } from '../../dev-tools.service';
import { MatDialogRef } from '@angular/material/dialog';
import { selectZoneState$, selectZonesLoaded$ } from 'src/app/store/selectors/zone.selector';
import { Zone } from 'src/app/models/zone';
import moment from 'moment';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { Family, ParentFamily } from 'src/app/models/family';
import { selectParentFamilyArray$, selectParentFamilyLoaded$ } from 'src/app/store/selectors/parent-family.selectors';
import { PlannedLine } from 'src/app/models/dev-tools.model';
import { Scope } from 'src/app/models/profile';
import { selectFamiliesByParentFamilyIds$, selectFamilyLoaded$, selectSectionByFamilyId$ } from 'src/app/store/selectors/family.selectors';
import { ReducedZone, ZoneService } from 'src/app/services/zone.service';
import { TranslateService } from '@ngx-translate/core';
import { Asset } from 'src/app/models/asset';
import { selectAssetLoaded$, selectAssetsByFamiliesIds } from 'src/app/store/selectors/asset.selectors';

export enum OperationType {
    load = 'load',
    unload = 'unload',
    arrival = 'arrival',
    departure = 'departure',
}

@Component({
    selector: 'app-dialog-generate-plannings-line',
    templateUrl: './dialog-generate-plannings-line.component.html',
    styleUrls: ['./dialog-generate-plannings-line.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DialogGeneratePlanningsLineComponent implements OnInit {

    public inputLoading: Record<string, boolean> = {
        zones: false,
        familyType: false,
        family: false,
    };

    public eventForm: FormGroup<{
        operationType: FormControl<OperationType>;
        parentFamily: FormControl<string>;
        section: FormControl<string>;
        msn: FormControl<string>;
        plannedDate: FormControl<moment.Moment>;
        plannedLocation: FormControl<Zone['id']>;
        assetFamily: FormControl<string>;
        asset: FormControl<string>;
    }> = this._fb.group({
        operationType: this._fb.control(OperationType.load, Validators.required),
        parentFamily: this._fb.control(null, [Validators.required]),
        section: this._fb.control(null),
        msn: this._fb.control(null, [Validators.pattern(/^\d{5}$/)]),
        plannedDate: this._fb.control(moment(), [Validators.required]),
        plannedLocation: this._fb.control(null),
        assetFamily: this._fb.control(null, [Validators.required]),
        asset: this._fb.control(null),
    });

    public searchForm: FormGroup<{
        parentFamily: FormControl<string>;
        section: FormControl<string>;
        plannedLocation: FormControl<string>;
        assetFamily: FormControl<string>;
        asset: FormControl<string>;
    }> = this._fb.group({
        parentFamily: this._fb.control(''),
        section: this._fb.control(''),
        plannedLocation: this._fb.control(''),
        assetFamily: this._fb.control(''),
        asset: this._fb.control(''),
    });

    public readonly OperationType = OperationType;
    // Loading states
    public loadings = {
        parentFamilies$: this.store.select(selectParentFamilyLoaded$).pipe(
            map(loaded => !loaded),
            tap(() => this._cd.markForCheck()),
        ),
        families$: this.store.select(selectFamilyLoaded$).pipe(
            map(loaded => !loaded),
            tap(() => this._cd.markForCheck()),
        ),
        assets$: this.store.select(selectAssetLoaded$).pipe(
            map(loaded => !loaded),
            tap(() => this._cd.markForCheck()),
        ),
        zones$: this.store.select(selectZonesLoaded$).pipe(
            map((loaded) => !loaded),
            tap(() => this._cd.markForCheck()),
        ),
        submit: false,
    };

    public parentFamilies$: Observable<ParentFamily[]> = this.store.select(selectParentFamilyArray$).pipe(
        map(pf => pf.filter(({ scope }) => scope === Scope.OVERSIZE)),
    );

    public families$: Observable<Family[]> = this.eventForm.controls.parentFamily.valueChanges.pipe(
        startWith(this.eventForm.value.parentFamily),
        switchMap(parentFamily => iif(
            () => !!parentFamily,
            this.store.select(selectFamiliesByParentFamilyIds$([parentFamily])),
            of([])
        )),
        tap(families => this._families = families),
    );

    public assets$: Observable<Asset[]> = this.eventForm.controls.assetFamily.valueChanges.pipe(
        startWith(this.eventForm.value.assetFamily),
        switchMap(assetFamily => iif(
            () => !!assetFamily,
            this.store.select(selectAssetsByFamiliesIds([assetFamily])),
            of([])
        )),
        tap(assets => this._assets = assets),
    );

    public section$: Observable<Family['compatibleSection']> = this.eventForm.controls.assetFamily.valueChanges.pipe(
        startWith(this.eventForm.value.assetFamily),
        switchMap(assetFamily => iif(
            () => !!assetFamily,
            this.store.select(selectSectionByFamilyId$(assetFamily)),
            of([])
        )),
    );

    public zones$: Observable<ReducedZone[]> = this.store.select(selectZoneState$).pipe(
        map(zone => this._zoneService.reduceZoneWithLevels(zone.data))
    );

    private _subscription: Subscription = new Subscription();
    private _families: Family[];
    private _assets: Asset[];

    constructor(
        private store: Store,
        private _devToolsService: DevToolsService,
        private _snackBar: SnackbarComponent,
        private _errorTranslationService: ErrorTranslationService,
        public dialogRef: MatDialogRef<DialogGeneratePlanningsLineComponent>,
        private readonly _fb: FormBuilder,
        private readonly _cd: ChangeDetectorRef,
        private readonly _translate: TranslateService,
        private _zoneService: ZoneService,
    ) { }

    ngOnInit(): void {
        this._subscription.add(
            this.eventForm.controls.parentFamily.valueChanges.pipe(
                startWith(null),
                distinctUntilChanged(),
            ).subscribe(
                value => {
                    this.eventForm.controls.assetFamily[value ? 'enable' : 'disable']();
                    this.eventForm.patchValue({ assetFamily: null });
                }
            )
        );
        this._subscription.add(
            this.eventForm.controls.assetFamily.valueChanges.pipe(
                startWith(null),
                distinctUntilChanged(),
            ).subscribe(
                value => {
                    this.eventForm.controls.section[value ? 'enable' : 'disable']();
                    this.eventForm.controls.asset[value ? 'enable' : 'disable']();
                    this.eventForm.controls.msn[value ? 'enable' : 'disable']();
                    this.eventForm.patchValue({ section: null, msn: null });
                }
            )
        );
    }

    // /** Generate event API call */
    public async generateEvent() {
        const { value } = this.eventForm;
        const plannedLine: PlannedLine = {
            jig_name: await this._buildPlannedProperty(value, 'jig_name') as PlannedLine['jig_name'],
            operation_type: value.operationType,
            planned_time: value.plannedDate.toISOString(),
            planned_location: value.plannedLocation,
            transport_family_id: value.assetFamily,
            transport_means: await this._buildPlannedProperty(value, 'transport_means') as PlannedLine['transport_means'],
        };
        this.loadings.submit = true;
        console.log('Planned Line to be sent:', plannedLine);
        this._subscription.add(this._devToolsService.postPlannedLine(plannedLine).subscribe({
            next: () => {
                this._snackBar.open(this._translate.instant('EVENT_GENERATED'), 'green-snackbar', 5000);
                this.dialogRef.close();
            },
            error: (err) => {
                this._errorTranslationService.handleError(err, 'BANNER_FAIL_INTERNAL_CREATE');
                this.loadings.submit = false;
            },
            complete: () => {
                this.loadings.submit = false;
            }
        }));
    }

    private async _buildPlannedProperty(
        formData: typeof this.eventForm.value,
        property: keyof PlannedLine
    ): Promise<PlannedLine[typeof property]> {
        switch (property) {
            case 'jig_name':
                if ([OperationType.load, OperationType.unload].includes(formData.operationType)) {
                    return {
                        section_msn: [{
                            msn: formData.msn,
                            section: formData.section,
                        }],
                    };
                }
                return;
            case 'transport_means':
                return {
                    family_id: formData.assetFamily,
                    family_name: this._families.find(
                        ({ id }) => id === formData.assetFamily
                    )?.name,
                    asset_id: formData.asset,
                    asset_name: this._assets.find(
                        ({ id }) => id === formData.asset
                    )?.name,
                };
        }
    }

    public trackById(item) {
        return item.id;
    }

}
